import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { teacherImageList } from '../../../constants/teacherImages';

const TeachersSection = () => {
  const navigate = useNavigate();
  const API_URL = import.meta.env.VITE_API_URL;
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['teacherList'],
      queryFn: async ({ pageParam = 1 }) => {
        const res = await fetch(
          `${API_URL}/bbs/ajax.get_teacher_list.php?page=${pageParam}`
        );
        return res.json();
      },
      getNextPageParam: (lastPage) => {
        const currentPage = lastPage.data.pagination.page;
        const totalPage = lastPage.data.pagination.total_page;
        return currentPage < totalPage ? currentPage + 1 : undefined;
      },
      initialPageParam: 1
    });

  const items = (data?.pages.flatMap((page: any) => page.data.list) ?? []).map(
    (item: any) => ({ ...item, file: teacherImageList[item.name] || null })
  );

  return (
    <section
      id="teachers"
      className="py-20 bg-gradient-to-br from-blue-50 to-purple-50"
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
            최고의 <span className="text-blue-600">AI 교육 전문가</span>들
          </h2>
          <p className="md:text-xl text-lg text-gray-600 max-w-3xl mx-auto">
            풍부한 경험과 AI 교육 전문성을 갖춘 선생님들이 아이들의 성장을
            도와드립니다
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 shadow-lg flex flex-col items-center justify-center text-center">
              <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center mb-4 border-4 border-gray-100">
                <i className="ri-user-line text-4xl text-gray-400"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-400 mb-2">
                등록된 선생님이 없습니다
              </h3>
              <div className="text-gray-400 mb-6">
                곧 새로운 강사가 추가될 예정입니다.
              </div>
              <button
                disabled
                className="w-full bg-gray-300 text-white py-3 rounded-full cursor-not-allowed font-semibold"
              >
                Coming Soon
              </button>
            </div>
          ) : (
            items.map((item: any) => (
              <div
                key={item.id}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="text-center mb-6">
                  <div className="relative inline-block">
                    {item.file ? (
                      <img
                        src={item.file}
                        alt={item.name}
                        className="w-24 h-24 rounded-full object-cover mx-auto mb-4 border-4 border-blue-100"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center mb-4 border-4 border-gray-100">
                        <i className="ri-user-line text-4xl text-gray-400"></i>
                      </div>
                    )}
                    <div className="absolute bottom-2 right-2 bg-green-500 w-6 h-6 rounded-full border-2 border-white"></div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {item.name} 선생님
                  </h3>
                  <div className="text-blue-600 font-semibold mb-1">
                    {item.specialties.join(', ')}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mb-6 justify-center">
                  {item.careers.map((career: string, index: number) => (
                    <span
                      key={index}
                      className="bg-purple-100 text-purple-600 px-3 py-1 rounded-full text-sm font-medium"
                    >
                      {career}
                    </span>
                  ))}
                </div>
                <button 
                  onClick={() => {
                    if(item.recentClassLink) {
                      navigate(item.recentClassLink);
                    } else {
                      alert("준비중입니다.");
                    }
                  }}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-full hover:from-blue-600 hover:to-purple-700 transition-all font-semibold whitespace-nowrap">
                  <i className="ri-calendar-line mr-2"></i>
                  수업스케줄 보기
                </button>
              </div>
            ))
          )}
        </div>
        <div className="text-center mt-16">
          {hasNextPage && (
            <button
              onClick={() => fetchNextPage()}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-4 rounded-full text-lg font-bold hover:from-blue-600 hover:to-purple-700 transition-all transform hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-team-line mr-2"></i>
              선생님 더 보기
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default TeachersSection;
